/* ============ count deviations ============
   True-count index plays, keyed by counting system (per-system indices). Two
   representations per system: display tables for the Deviations drawer, and
   machine tables that drive the count-aware grader (recommendCount).

   Populated systems: Hi-Lo (Schlesinger's Illustrious 18 + Fab 4) and Zen
   (Snyder's Sweet 16 + Fab 4, verified against the Blackbelt Zen chart). Other
   BALANCED systems have no verified index set yet, so recommendCount falls back
   to BASIC STRATEGY for them rather than mis-applying another system's numbers.
   KO is unbalanced (tc === null) and has always been deviation-free. See
   PER-SYSTEM-INDICES-DESIGN.md and ZEN-INDICES-RESEARCH.md.

   Convention (validated): compare the true count with `tc >= idx` — identical
   to the published "act if the TC equals or exceeds the index" rule (integer
   indices ⇒ same as flooring). Deck estimation is half-deck, the basis the
   indices were simulated on. Numbers are per-deck true counts (RC / decks
   remaining) — Zen's chart is quarter-deck, so we use Snyder's per-deck
   conversion (≈4× the chart), not the chart values.

   Per-rule indices: each machine `idx` is `{ s17, h17 }` — many cells differ by
   the dealer's soft-17 rule. `h17: null` means "no deviation under H17" (the
   play is already basic strategy there) and the grader falls through to basic —
   this replaces the old `s17Only` flag. */

import { hv, pairRank } from "./cards.js";
import { hardAction, softAction, surrAction, pairAction, recommend } from "./strategy.js";

/* ======================= Hi-Lo ======================= */
/* ---- display tables (the Deviations drawer) ---- */
const HILO_I18 = [
  { hand: "Insurance", vs: "Ace up", idx: "+3", play: "Take it", alt: "Decline" },
  { hand: "16",    vs: "10", idx: "0",  play: "Stand",  alt: "Hit" },
  { hand: "15",    vs: "10", idx: "+4", play: "Stand",  alt: "Hit" },
  { hand: "10,10", vs: "5",  idx: "+5", play: "Split",  alt: "Stand" },
  { hand: "10,10", vs: "6",  idx: "+4", play: "Split",  alt: "Stand" },
  { hand: "10",    vs: "10", idx: "+4", play: "Double", alt: "Hit" },
  { hand: "12",    vs: "3",  idx: "+2", play: "Stand",  alt: "Hit" },
  { hand: "12",    vs: "2",  idx: "+3", play: "Stand",  alt: "Hit" },
  { hand: "11",    vs: "A",  idx: { s17: "+1", h17: null }, play: "Double", alt: "Hit" },
  { hand: "9",     vs: "2",  idx: "+1", play: "Double", alt: "Hit" },
  { hand: "10",    vs: "A",  idx: "+4", play: "Double", alt: "Hit" },
  { hand: "9",     vs: "7",  idx: "+3", play: "Double", alt: "Hit" },
  { hand: "16",    vs: "9",  idx: "+5", play: "Stand",  alt: "Hit" },
  { hand: "13",    vs: "2",  idx: "−1", play: "Stand",  alt: "Hit" },
  { hand: "12",    vs: "4",  idx: "0",  play: "Stand",  alt: "Hit" },
  { hand: "12",    vs: "5",  idx: "−2", play: "Stand",  alt: "Hit" },
  { hand: "12",    vs: "6",  idx: "−1", play: "Stand",  alt: "Hit" },
  { hand: "13",    vs: "3",  idx: "−2", play: "Stand",  alt: "Hit" },
];

const HILO_FAB4 = [
  { hand: "14", vs: "10", idx: "+3", play: "Surrender", alt: "Hit" },
  { hand: "15", vs: "10", idx: "0",  play: "Surrender", alt: "Hit" },
  { hand: "15", vs: "9",  idx: "+2", play: "Surrender", alt: "Hit" },
  { hand: "15", vs: "A",  idx: { s17: "+1", h17: null }, play: "Surrender", alt: "Hit" },
];

/* ---- machine tables (the grader). idx per-rule { s17, h17 }; h17:null ⇒ basic. */
const HILO_DEV = [
  { pair: "10", up: 5,  idx: { s17: 5,  h17: 5  },    play: "P", below: "S", label: "10,10 v 5" },
  { pair: "10", up: 6,  idx: { s17: 4,  h17: 4  },    play: "P", below: "S", label: "10,10 v 6" },
  { t: 16, up: 10, idx: { s17: 0,  h17: 0  },    play: "S", below: "H", label: "16 v 10" },
  { t: 16, up: 9,  idx: { s17: 5,  h17: 5  },    play: "S", below: "H", label: "16 v 9" },
  { t: 15, up: 10, idx: { s17: 4,  h17: 4  },    play: "S", below: "H", label: "15 v 10" },
  { t: 13, up: 2,  idx: { s17: -1, h17: -1 },    play: "S", below: "H", label: "13 v 2" },
  { t: 13, up: 3,  idx: { s17: -2, h17: -2 },    play: "S", below: "H", label: "13 v 3" },
  { t: 12, up: 2,  idx: { s17: 3,  h17: 3  },    play: "S", below: "H", label: "12 v 2" },
  { t: 12, up: 3,  idx: { s17: 2,  h17: 2  },    play: "S", below: "H", label: "12 v 3" },
  { t: 12, up: 4,  idx: { s17: 0,  h17: 0  },    play: "S", below: "H", label: "12 v 4" },
  { t: 12, up: 5,  idx: { s17: -2, h17: -2 },    play: "S", below: "H", label: "12 v 5" },
  { t: 12, up: 6,  idx: { s17: -1, h17: -1 },    play: "S", below: "H", label: "12 v 6" },
  { t: 11, up: 11, idx: { s17: 1,  h17: null },  play: "D", below: "H", label: "11 v A" },
  { t: 10, up: 10, idx: { s17: 4,  h17: 4  },    play: "D", below: "H", label: "10 v 10" },
  { t: 10, up: 11, idx: { s17: 4,  h17: 4  },    play: "D", below: "H", label: "10 v A" },
  { t: 9,  up: 2,  idx: { s17: 1,  h17: 1  },    play: "D", below: "H", label: "9 v 2" },
  { t: 9,  up: 7,  idx: { s17: 3,  h17: 3  },    play: "D", below: "H", label: "9 v 7" },
];

const HILO_DEV_SURR = [
  { t: 14, up: 10, idx: { s17: 3, h17: 3 },    label: "14 v 10" },
  { t: 15, up: 9,  idx: { s17: 2, h17: 2 },    label: "15 v 9" },
  { t: 15, up: 10, idx: { s17: 0, h17: 0 },    label: "15 v 10" },
  { t: 15, up: 11, idx: { s17: 1, h17: null }, label: "15 v A" },
];

const HILO_DISPLAY = {
  setName: "Illustrious 18",
  footnote:
    "Under H17 rules, 11 vs A (double) and 15 vs A (surrender) are already basic " +
    "strategy, so they show no index (“—”) at your current table. " +
    "Insurance at TC +3 is the single most valuable deviation on the list.",
};

/* ======================= Zen ======================= */
/* Snyder's Sweet 16 + Fab 4. Per-deck true counts (S17 basis; H17 where it
   differs). Verified against the Blackbelt Zen chart (see ZEN-INDICES-
   RESEARCH.md). Zen's Sweet 16 has no pair-split index. */
const ZEN_I18 = [
  { hand: "Insurance", vs: "Ace up", idx: "+5", play: "Take it", alt: "Decline" },
  { hand: "16", vs: "9",  idx: "+8",    play: "Stand",  alt: "Hit" },
  { hand: "16", vs: "10", idx: "0",     play: "Stand",  alt: "Hit" },
  { hand: "15", vs: "10", idx: "+6",    play: "Stand",  alt: "Hit" },
  { hand: "13", vs: "2",  idx: "−2",    play: "Stand",  alt: "Hit" },
  { hand: "13", vs: "3",  idx: "−4",    play: "Stand",  alt: "Hit" },
  { hand: "12", vs: "2",  idx: "+6",    play: "Stand",  alt: "Hit" },
  { hand: "12", vs: "3",  idx: "+3",    play: "Stand",  alt: "Hit" },
  { hand: "12", vs: "4",  idx: "+1",    play: "Stand",  alt: "Hit" },
  { hand: "12", vs: "5",  idx: "−2",    play: "Stand",  alt: "Hit" },
  { hand: "12", vs: "6",  idx: { s17: "−1", h17: "−5" }, play: "Stand",  alt: "Hit" },
  { hand: "9",  vs: "2",  idx: "+2",    play: "Double", alt: "Hit" },
  { hand: "9",  vs: "7",  idx: "+7",    play: "Double", alt: "Hit" },
  { hand: "10", vs: "10", idx: "+7",    play: "Double", alt: "Hit" },
  { hand: "10", vs: "A",  idx: { s17: "+5", h17: "+4" }, play: "Double", alt: "Hit" },
  { hand: "11", vs: "A",  idx: { s17: "+1", h17: "−2" }, play: "Double", alt: "Hit" },
];

const ZEN_FAB4 = [
  { hand: "14", vs: "10", idx: "+4",   play: "Surrender", alt: "Hit" },
  { hand: "15", vs: "9",  idx: "+4",   play: "Surrender", alt: "Hit" },
  { hand: "15", vs: "10", idx: "0",    play: "Surrender", alt: "Hit" },
  { hand: "15", vs: "A",  idx: { s17: "+4", h17: "0" }, play: "Surrender", alt: "Hit" },
];

const ZEN_DEV = [
  { t: 16, up: 9,  idx: { s17: 8,  h17: 8  },  play: "S", below: "H", label: "16 v 9" },
  { t: 16, up: 10, idx: { s17: 0,  h17: 0  },  play: "S", below: "H", label: "16 v 10" },
  { t: 15, up: 10, idx: { s17: 6,  h17: 6  },  play: "S", below: "H", label: "15 v 10" },
  { t: 13, up: 2,  idx: { s17: -2, h17: -2 },  play: "S", below: "H", label: "13 v 2" },
  { t: 13, up: 3,  idx: { s17: -4, h17: -4 },  play: "S", below: "H", label: "13 v 3" },
  { t: 12, up: 2,  idx: { s17: 6,  h17: 6  },  play: "S", below: "H", label: "12 v 2" },
  { t: 12, up: 3,  idx: { s17: 3,  h17: 3  },  play: "S", below: "H", label: "12 v 3" },
  { t: 12, up: 4,  idx: { s17: 1,  h17: 1  },  play: "S", below: "H", label: "12 v 4" },
  { t: 12, up: 5,  idx: { s17: -2, h17: -2 },  play: "S", below: "H", label: "12 v 5" },
  { t: 12, up: 6,  idx: { s17: -1, h17: -5 },  play: "S", below: "H", label: "12 v 6" },
  { t: 9,  up: 2,  idx: { s17: 2,  h17: 2  },  play: "D", below: "H", label: "9 v 2" },
  { t: 9,  up: 7,  idx: { s17: 7,  h17: 7  },  play: "D", below: "H", label: "9 v 7" },
  { t: 10, up: 10, idx: { s17: 7,  h17: 7  },  play: "D", below: "H", label: "10 v 10" },
  { t: 10, up: 11, idx: { s17: 5,  h17: 4  },  play: "D", below: "H", label: "10 v A" },
  { t: 11, up: 11, idx: { s17: 1,  h17: -2 },  play: "D", below: "H", label: "11 v A" },
];

const ZEN_DEV_SURR = [
  { t: 14, up: 10, idx: { s17: 4, h17: 4 }, label: "14 v 10" },
  { t: 15, up: 9,  idx: { s17: 4, h17: 4 }, label: "15 v 9" },
  { t: 15, up: 10, idx: { s17: 0, h17: 0 }, label: "15 v 10" },
  { t: 15, up: 11, idx: { s17: 4, h17: 0 }, label: "15 v A" },
];

const ZEN_DISPLAY = {
  setName: "Sweet 16",
  footnote:
    "Per-deck true counts (Snyder's Zen). Insurance at TC +5 is the most " +
    "valuable deviation on the list.",
};

/* ---- per-system index sets ----
   To add a system, append a VERIFIED entry of the same shape (display
   setName/footnote + I18/FAB4 + machine DEV/DEV_SURR) — see PER-SYSTEM-INDICES-
   DESIGN.md for sourcing and the TC convention the numbers must match. A system
   absent from this map gets no deviations (pure basic strategy). */
export const INDICES = {
  "Hi-Lo": { display: HILO_DISPLAY, I18: HILO_I18, FAB4: HILO_FAB4, DEV: HILO_DEV, DEV_SURR: HILO_DEV_SURR },
  "Zen":   { display: ZEN_DISPLAY,  I18: ZEN_I18,  FAB4: ZEN_FAB4,  DEV: ZEN_DEV,  DEV_SURR: ZEN_DEV_SURR },
};

/* The active system's index set, or null when it has no verified indices (the
   Deviations drawer then shows a "not included yet" note). */
export function indicesFor(system) {
  return INDICES[system] ?? null;
}

/* Per-system insurance index (the dealer-Ace peek deviation), read from the
   verified I18/Sweet-16 table so it never drifts from what we display:
   Hi-Lo +3, Zen +5. Returns null for systems with no verified set (the caller
   falls back to +3). */
export function insuranceIndex(system) {
  const set = INDICES[system];
  if (!set) return null;
  const row = set.I18.find((r) => r.hand === "Insurance");
  // Display strings use the typographic minus (U+2212), which parseInt reads
  // as NaN — normalize, and return null (→ caller's fallback) for anything
  // non-finite rather than letting NaN poison the tc >= idx comparison.
  const n = row ? parseInt(String(row.idx).replace("\u2212", "-"), 10) : NaN;
  return Number.isFinite(n) ? n : null;
}

/* Resolve a machine entry's index for the active soft-17 rule. Returns the
   number, or null when the cell carries no deviation under that rule (h17:null)
   — the grader then falls through to basic strategy for that hand. */
function ruleIdx(entry, st) {
  const v = st.h17 ? entry.idx.h17 : entry.idx.s17;
  return v == null ? null : v;
}

/* count-aware recommendation: deviations override basic strategy when the TC
   crosses the index. tc === null (unbalanced) OR a system with no verified
   index set → pure basic strategy (we never apply another system's indices). */
export function recommendCount(hand, up, availability, st, tc) {
  const sys = INDICES[st.system];
  if (tc === null || !sys) {
    return { action: recommend(hand, up, availability, st), deviation: false };
  }
  const { DEV, DEV_SURR } = sys;
  const cards = hand.cards;
  const { total, soft } = hv(cards);
  const isPair = cards.length === 2 && pairRank(cards[0]) === pairRank(cards[1]);
  // Deck-aware basic (engine v5). Two subtleties:
  //   1. Each dev cell anchors one side to 4-8 deck basic (`below` for
  //      positive indices, `play` for negative ones like 12v4's hit-below-0).
  //      At 1-2 decks that anchor can be stale - single-deck basic already
  //      doubles 9v2 - so when the table hands back the multi-deck anchor and
  //      the deck's own basic differs, we play deck basic instead. At 4-8
  //      decks multiBasic === basic and behavior is exactly as before.
  //   2. The `deviation` flag is honest: true only when the chosen action
  //      differs from deck basic, so an index that merely coincides with
  //      basic isn't labeled a deviation.
  const basic = recommend(hand, up, availability, st);
  const multiBasic =
    st.decks <= 2 ? recommend(hand, up, availability, { ...st, decks: 6 }) : basic;
  const anchor = (a) => (a === multiBasic && a !== basic ? basic : a);

  if (isPair && availability.canSplit) {
    const pd = DEV.find((x) => x.pair === pairRank(cards[0]) && x.up === up);
    const pi = pd ? ruleIdx(pd, st) : null;
    if (pd && pi != null) {
      const a = anchor(tc >= pi ? pd.play : pd.below);
      return { action: a, deviation: a !== basic, label: pd.label, idx: pi };
    }
    const pa = pairAction(pairRank(cards[0]), up, st);
    if (pa === "P" || pa === "Rp" || pa === "Rs" || pa === "Rh") {
      return { action: basic, deviation: false };
    }
  }

  if (availability.canSurrender && !soft && cards.length === 2) {
    const sd = DEV_SURR.find((x) => x.t === total && x.up === up);
    const si = sd ? ruleIdx(sd, st) : null;
    if (sd && si != null) {
      if (tc >= si) return { action: "R", deviation: basic !== "R", label: sd.label, idx: si };
      // below the index: deliberately no surrender — fall through to hit/stand logic
    } else {
      const r = surrAction(total, up, st, soft);
      if (r) return { action: "R", deviation: false };
    }
  }

  if (!soft) {
    const hd = DEV.find((x) => !x.pair && x.t === total && x.up === up);
    const hi = hd ? ruleIdx(hd, st) : null;
    if (hd && hi != null) {
      let a = anchor(tc >= hi ? hd.play : hd.below);
      if (a === "D" && !availability.canDouble) a = "H";
      return { action: a, deviation: a !== basic, label: hd.label, idx: hi };
    }
  }

  let a = soft ? softAction(total, up, st) : hardAction(total, up, st);
  if (a === "D") a = availability.canDouble ? "D" : "H";
  else if (a === "Ds") a = availability.canDouble ? "D" : "S";
  return { action: a, deviation: false };
}
